import { useEffect, useMemo, useState } from 'react';
import { listRequests } from '../../api/originatorApi';
import { CreateRequestModal } from '../../components/requests/CreateRequestModal';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { KpiCard } from '../../components/ui/KpiCard';
import { Button } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { IconInbox } from '../../components/ui/icons';
import { formatDate } from '../../utils/format';
import { FEATURES } from '../../config/features';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'CREATED', label: 'Created' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// The requests list is keyed by the workflow ticket type. Transfer-engine
// transactions (custody / control / assignment) all surface as TRANSFER here.
const TYPE_TABS = [
  { value: '', label: 'All types' },
  { value: 'COPY_REQUEST', label: 'Copy request' },
  { value: 'TRANSFER', label: 'Transfer' },
  { value: 'PLEDGE', label: 'Pledge' },
  { value: 'RELEASE', label: 'Release' },
];

const PAGE_SIZE_DEFAULT = 20;

export function RequestsPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listRequests({
          page,
          pageSize,
          status: statusFilter || undefined,
          requestType: typeFilter || undefined,
        });
        if (cancelled) return;
        setRows(response.items || []);
        setTotal(response.meta?.total || 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load requests.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, statusFilter, typeFilter, reloadToken]);

  const columns = useMemo(
    () => [
      {
        key: 'id',
        header: 'Request',
        render: (row) => (
          <span className="font-mono text-[11px] font-semibold text-eorig-ink">
            {row.id}
          </span>
        ),
      },
      {
        key: 'asset_id',
        header: 'Asset',
        render: (row) => (
          <span className="font-mono text-[11px] text-eorig-muted">
            {row.asset_id}
          </span>
        ),
      },
      {
        key: 'request_type',
        header: 'Type',
        render: (row) => (
          <span className="inline-flex rounded-md bg-eorig-surface px-2 py-1 text-[11px] font-semibold text-eorig-navy-600">
            {row.request_type}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusPill value={row.status} />,
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (row) => (
          <span className="text-eorig-muted">{formatDate(row.created_at)}</span>
        ),
      },
      {
        key: 'updated_at',
        header: 'Updated',
        render: (row) => (
          <span className="text-eorig-muted">{formatDate(row.updated_at)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          label="Total requests"
          value={total}
          subline="Routed to the custodian"
          icon={IconInbox}
          accent
          loading={loading}
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <FilterTabs
            tabs={STATUS_TABS}
            value={statusFilter}
            ariaLabel="Filter by status"
            onChange={(value) => {
              setPage(1);
              setStatusFilter(value);
            }}
          />
          <div className="flex items-center gap-2">
            {FEATURES.listExports ? <Button variant="ghost">Export</Button> : null}
            <Button variant="gradient" onClick={() => setModalOpen(true)}>
              Manual request
            </Button>
          </div>
        </div>

        <FilterTabs
          tabs={TYPE_TABS}
          value={typeFilter}
          ariaLabel="Filter by request type"
          onChange={(value) => {
            setPage(1);
            setTypeFilter(value);
          }}
        />
      </div>

      {error ? (
        <ErrorBanner message={error} onRetry={() => setReloadToken((t) => t + 1)} />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No requests yet."
        minWidth={720}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />

      <CreateRequestModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={() => setReloadToken((t) => t + 1)}
      />
    </div>
  );
}
